import { useState } from "react";
import { toast } from "sonner";
import { Plus, MapPin, Phone, UserCog, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PageHeader, Empty } from "@/components/common";
import { ClinicForm, emptyClinic } from "@/components/ClinicForm";
import { PersonForm, emptyPerson } from "@/components/PersonForm";
import { useAuth } from "@/context/AuthContext";
import { LogoUploader } from "@/components/ClinicLogo";
import { api, errMsg } from "@/lib/api";

const MOD_LABEL = { procedures: "Procedures", dental: "Dental", insurance: "Insurance" };

function ClinicCard({ c, onEdit, onManager, onLogo }) {
  return (
    <div className="rise rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid={`clinic-card-${c.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div><h3 className="text-lg font-semibold text-slate-900">{c.name}</h3>
          <p className="mt-1 flex items-center gap-1 text-sm text-slate-500"><MapPin className="h-3.5 w-3.5" />{[c.city, c.country].filter(Boolean).join(", ")}</p></div>
        <span className="rounded-full bg-emerald-50 px-2.5 py-1 font-mono text-xs font-semibold text-emerald-700">{c.currency}</span>
      </div>
      <div className="mt-4 flex flex-wrap gap-1.5">
        {Object.entries(MOD_LABEL).map(([k, l]) => (
          <span key={k} className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${c.modules?.[k] ? "bg-teal-50 text-teal-700" : "bg-slate-100 text-slate-400 line-through"}`}>{l}</span>))}
        <span className="rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-600">Inpatient · soon</span>
      </div>
      <div className="mt-5"><LogoUploader clinic={c} onDone={onLogo} /></div>
      <div className="mt-5 rounded-xl bg-slate-50 p-4 text-sm">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Manager</p>
        {c.manager ? <div className="mt-1"><p className="font-semibold">{c.manager.name}</p><p className="text-slate-500">{c.manager.email}</p>{c.manager.phone && <p className="flex items-center gap-1 text-slate-500"><Phone className="h-3 w-3" />{c.manager.phone}</p>}</div>
          : <p className="mt-1 text-slate-400">Not appointed yet</p>}
      </div>
      <div className="mt-5 flex gap-2">
        <Button size="sm" variant="outline" className="rounded-full" onClick={onEdit} data-testid={`clinic-edit-button-${c.id}`}><Pencil className="mr-1 h-3.5 w-3.5" />Edit</Button>
        <Button size="sm" className="rounded-full" onClick={onManager} data-testid={`clinic-appoint-manager-button-${c.id}`}><UserCog className="mr-1 h-3.5 w-3.5" />{c.manager ? "Replace manager" : "Appoint manager"}</Button>
      </div>
    </div>
  );
}

export default function Clinics() {
  const { clinics, reloadClinics } = useAuth();
  const [form, setForm] = useState(null);
  const [mgr, setMgr] = useState(null);
  const saveClinic = async () => {
    try {
      const { id, manager, manager_id, org_id, created_at, created_by, logo_path, logo_updated_at, ...body } = form;
      if (id) await api.put(`/clinics/${id}`, body); else await api.post("/clinics", body);
      toast.success(id ? "Clinic updated" : "Clinic created");
      setForm(null);
      reloadClinics();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const saveMgr = async () => {
    try {
      await api.post(`/clinics/${mgr.clinicId}/manager`, mgr.person);
      toast.success("Manager appointed");
      setMgr(null);
      reloadClinics();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <PageHeader eyebrow="Your group" title="Clinics" subtitle="Open new clinics, choose the units they run and appoint a manager for each.">
        <Button className="rounded-full" onClick={() => setForm(emptyClinic)} data-testid="create-clinic-button"><Plus className="mr-2 h-4 w-4" />New clinic</Button>
      </PageHeader>
      {!clinics.length ? <Empty text="No clinics yet — create your first one." testId="clinics-empty" /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {clinics.map((c) => <ClinicCard key={c.id} c={c} onEdit={() => setForm(c)} onManager={() => setMgr({ clinicId: c.id, person: emptyPerson })} onLogo={reloadClinics} />)}
        </div>)}
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{form?.id ? "Edit clinic" : "New clinic"}</DialogTitle></DialogHeader>
          {form && <ClinicForm value={form} onChange={setForm} />}
          <DialogFooter><Button onClick={saveClinic} data-testid="clinic-form-submit-button">Save clinic</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!mgr} onOpenChange={(o) => !o && setMgr(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>Appoint clinic manager</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">The manager signs in with this email and temporary password, then appoints all other staff. Any previous manager is deactivated.</p>
          {mgr && <PersonForm value={mgr.person} onChange={(p) => setMgr({ ...mgr, person: p })} prefix="manager" />}
          <DialogFooter><Button onClick={saveMgr} data-testid="manager-form-submit-button">Appoint manager</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
